import type { Control, FieldPath, FieldValues, UseControllerProps } from 'react-hook-form';
import { useController } from 'react-hook-form';

import { NumericField } from './NumericField';

type NumericFormFieldProps<
  TFieldValues extends FieldValues,
  TFieldPath extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  control: Control<TFieldValues>;
  fieldPath: TFieldPath;
  className?: string | undefined;
  rules?: UseControllerProps<TFieldValues, TFieldPath>['rules'];
  onBlur?: () => void;
  onChange?: (value: number) => void;
  disabled?: boolean;
};

export const NumericFormField = <
  TFieldValues extends FieldValues,
  TFieldPath extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  fieldPath,
  className,
  control,
  rules,
  onBlur,
  onChange: parentOnChange,
  disabled,
}: NumericFormFieldProps<TFieldValues, TFieldPath>) => {
  const {
    field: { name, onBlur: innerOnBlur, onChange: formOnChange, ref, value },
  } = useController({ name: fieldPath, control, rules });

  const composedOnBlur = () => {
    innerOnBlur();
    onBlur?.();
  };

  return (
    <NumericField
      value={value}
      onChange={(newValue) => {
        formOnChange(newValue);
        if (newValue !== null) {
          parentOnChange?.(newValue);
        }
      }}
      ref={ref}
      name={name}
      onBlur={composedOnBlur}
      size={!className ? 'sm' : undefined}
      className={className}
      disabled={disabled}
    />
  );
};
